import {
  assignConversation,
  closeConversation,
  convertConversation,
  createConversationDraftOrder,
  issueConversationDiscount,
  listChatDiscounts,
  sendConversationInvoice,
  createInboxSocketTicket,
  getConversation,
  getConversationAttachment,
  getChatVisitor,
  getConversationContext,
  getConversationCounts,
  joinConversation,
  leaveConversation,
  listChatAgents,
  listChatDepartments,
  listChatPresence,
  listChatShortcuts,
  listConversationMessages,
  listLiveChatVisitors,
  listStoreHelpArticles,
  listOrgConversations,
  listOrgLiveChatVisitors,
  markConversationRead,
  renderChatShortcut,
  reopenConversation,
  sendConversationMessage,
  startProactiveConversation,
  trashConversation,
  updateConversation,
  uploadConversationAttachment,
  type AssignConversationRequest,
  type ChatAgent,
  type ChatDiscountOptions,
  type ChatDraftOrder,
  type CreateChatDraftOrderRequest,
  type ChatDepartment,
  type ChatPresenceList,
  type ChatShortcut,
  type ChatSocketTicket,
  type ChatVisitorProfile,
  type ConversationContextDetail,
  type ConversationCounts,
  type ConversationDetail,
  type ConvertConversationRequest,
  type HelpArticle,
  type CustomerUpload,
  type ListOrgConversations200,
  type ListOrgConversationsParams,
  type LiveChatVisitorList,
  type ProactiveConversation,
  type RenderedChatShortcut,
  type SendStaffChatMessageRequest,
  type StaffChatMessage,
  type StaffChatMessagePage,
  type StartProactiveConversationRequest,
  type UpdateConversationRequest,
} from '@nucleus-commerce/api-client/admin';

export type InboxListParams = ListOrgConversationsParams;

/** A page of the thread: `before` scrolls back, `after` catches up after a reconnect. */
export interface MessagePageParams {
  after?: string;
  before?: string;
  limit?: number;
}

/**
 * The live chat inbox (D-103). Lists are organisation-wide (`storeId` narrows them); everything on
 * one conversation is addressed by its store. Writes go over REST; the socket only wakes the page.
 */
export const conversationsApi = {
  list: (params: InboxListParams): Promise<ListOrgConversations200> => listOrgConversations(params),
  counts: (): Promise<ConversationCounts> => getConversationCounts(),
  get: (storeId: string, id: string): Promise<ConversationDetail> =>
    getConversation({ storeId, id }),
  messages: (
    storeId: string,
    id: string,
    params: MessagePageParams,
  ): Promise<StaffChatMessagePage> => listConversationMessages({ storeId, id }, params),
  context: (storeId: string, id: string): Promise<ConversationContextDetail> =>
    getConversationContext({ storeId, id }),
  send: (
    storeId: string,
    id: string,
    body: SendStaffChatMessageRequest,
  ): Promise<StaffChatMessage> => sendConversationMessage({ storeId, id }, body),
  assign: (
    storeId: string,
    id: string,
    body: AssignConversationRequest,
  ): Promise<ConversationDetail> => assignConversation({ storeId, id }, body),
  update: (
    storeId: string,
    id: string,
    body: UpdateConversationRequest,
  ): Promise<ConversationDetail> => updateConversation({ storeId, id }, body),
  /** Chat or offline message → ticket, in place (LC9). */
  convert: (
    storeId: string,
    id: string,
    body: ConvertConversationRequest,
  ): Promise<ConversationDetail> => convertConversation({ storeId, id }, body),
  join: (storeId: string, id: string): Promise<ConversationDetail> =>
    joinConversation({ storeId, id }),
  leave: (storeId: string, id: string): Promise<ConversationDetail> =>
    leaveConversation({ storeId, id }),
  close: (storeId: string, id: string): Promise<ConversationDetail> =>
    closeConversation({ storeId, id }),
  reopen: (storeId: string, id: string): Promise<ConversationDetail> =>
    reopenConversation({ storeId, id }),
  trash: (storeId: string, id: string): Promise<void> => trashConversation({ storeId, id }),
  markRead: (storeId: string, id: string, upToMessageId: string): Promise<ConversationDetail> =>
    markConversationRead({ storeId, id }, { upToMessageId }),
  /** The raw file is the body, typed by its own Content-Type; the answer's token goes in `attachments`. */
  upload: (storeId: string, id: string, file: File): Promise<CustomerUpload> =>
    uploadConversationAttachment(
      { storeId, id },
      { filename: file.name },
      { data: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } },
    ),
  /** Fetched through the client so the session travels with it; a plain link would be refused. */
  attachment: async (storeId: string, id: string, uploadId: string): Promise<Blob> => {
    const body: unknown = await getConversationAttachment(
      { storeId, id, uploadId },
      { responseType: 'blob', headers: { Accept: '*/*' } },
    );
    if (!(body instanceof Blob)) throw new TypeError('Attachment download did not return a Blob');
    return body;
  },
  shortcuts: async (storeId: string): Promise<ChatShortcut[]> =>
    (await listChatShortcuts({ storeId })).items,
  renderShortcut: (
    storeId: string,
    conversationId: string,
    id: string,
  ): Promise<RenderedChatShortcut> => renderChatShortcut({ storeId, conversationId, id }),
  departments: async (storeId: string): Promise<ChatDepartment[]> =>
    (await listChatDepartments({ storeId })).items,
  presence: (storeId: string): Promise<ChatPresenceList> => listChatPresence({ storeId }),
  /** Staff who can work this store's chat (LCW1); `canReply` marks who can be assigned. No emails. */
  agents: async (storeId: string): Promise<ChatAgent[]> =>
    (await listChatAgents({ storeId })).items,
  socketTicket: (): Promise<ChatSocketTicket> => createInboxSocketTicket(),
  /** Shoppers on one store's storefront now (LC7), refetched every 30 s. */
  visitors: (storeId: string): Promise<LiveChatVisitorList> => listLiveChatVisitors({ storeId }),
  /** One visitor's profile with the current visit and the pages opened (tawk.to's visitor details). */
  visitor: (storeId: string, id: string): Promise<ChatVisitorProfile> =>
    getChatVisitor({ storeId, id }),
  /** The same across every store the member can access (LCW1); `storeId` narrows it to one. */
  orgVisitors: (storeId: string | undefined): Promise<LiveChatVisitorList> =>
    listOrgLiveChatVisitors(storeId ? { storeId } : undefined),
  /** 409 with `meta.conversationId` when the visitor already has an open chat; 403 VISITOR_BANNED. */
  startChat: (
    storeId: string,
    visitorId: string,
    body: StartProactiveConversationRequest,
  ): Promise<ProactiveConversation> => startProactiveConversation({ storeId, id: visitorId }, body),
  /**
   * Published help articles matching the words typed after `?` (LC8). Only live ones carry a
   * `url`, so internal articles never reach a reply.
   */
  helpArticles: async (storeId: string, q: string): Promise<Array<HelpArticle & { url: string }>> =>
    (await listStoreHelpArticles({ storeId }, { q, status: 'published', limit: 8 })).items.filter(
      (article): article is HelpArticle & { url: string } => article.url !== null,
    ),
  /** Code discounts the member may hand out, with what is left of their daily cap (LC12). */
  discounts: (storeId: string): Promise<ChatDiscountOptions> => listChatDiscounts({ storeId }),
  /** A single-use code posted as a discount card; 422 LIMIT_EXCEEDED past the daily cap. */
  issueDiscount: (storeId: string, id: string, discountId: string): Promise<StaffChatMessage> =>
    issueConversationDiscount({ storeId, id }, { discountId }),
  /** A draft order from the thread's product cards (and a deposit); needs orders.write. */
  draftOrder: (
    storeId: string,
    id: string,
    body: CreateChatDraftOrderRequest,
  ): Promise<ChatDraftOrder> => createConversationDraftOrder({ storeId, id }, body),
  /** Emails the latest draft's invoice and posts it as an invoice card. */
  sendInvoice: (storeId: string, id: string): Promise<StaffChatMessage> =>
    sendConversationInvoice({ storeId, id }, {}),
};
